import {Config} from '../config.js';
import {getCookie, setCookie} from './cookies.js';
import {user} from 'uber-sdk/user.js';
import {saveGame} from 'uber-sdk/savegame.js';
import {leaderboards} from 'uber-sdk/leaderboards.js';

/** Get amount of XP required for given level */
export function getXPForLevel(lvl: number) {
    /* Subtracting XP_INC to xp ensures we start at Level 1 */
    return Config.xpInc * 0.5 * (2 - lvl + lvl * lvl) - Config.xpInc;
}

/** Get level for current XP value */
export function getLevelForXP(xp: number) {
    /* Adding XP_INC to xp ensures we start at Level 1 */
    return Math.floor(0.5 * (1 + Math.sqrt(8 * ((xp + Config.xpInc) / Config.xpInc) - 7)));
}

/** Get percentage of XP collected required for the next level */
export function getXPPercentToNext(xp: number): number {
    const level = getLevelForXP(xp);
    const xpThis = getXPForLevel(level);
    const xpNext = getXPForLevel(level + 1);
    return (xp - xpThis) / (xpNext - xpThis);
}

export class Profile {
    async init() {
        const p = leaderboards
            .getScores('player-xp', 1)
            .then((s) => {
                this.xp = s[0].score;
                if (this.xp === 0) {
                    /* User is new, he might have some xp in a cookie */
                    this.getXPFromCookie();
                }
            })
            .catch(() => {
                this.getXPFromCookie();
            });

        /* Await all user providers */
        for (let u of user.providers) {
            if ('onReady' in u) await u.onReady;
        }

        return await Promise.all([p, this.load()]);
    }

    private getXPFromCookie() {
        this.xp = Number.parseInt(getCookie('paintball-xp'));
        if (Number.isNaN(this.xp)) this.xp = 0;
    }

    username: string = 'Guest' + (Math.random() * 99999).toFixed(0);
    avatar: string = 'Dummy';
    xp: number = 0;

    getUsername(): string {
        return this.username;
    }

    setUsername(u: string): void {
        if (user.isLoggedIn) {
            return;
        }
        this.username = u;
        this.save();
    }

    getAvatar(): string {
        return this.avatar;
    }

    setAvatar(a: string): void {
        this.avatar = a;
        this.save();
    }

    async load() {
        /* Cookie is loading from old cookies for backwards compatibility */
        const defaults = {
            avatar: user.user?.avatarUrl ?? getCookie('paintball-avatar') ?? 'Dummy',
            username: user.user?.name ?? (getCookie('paintball-username') || this.username),
        };
        // FIXME: null should already be handled, but load doesn't do the right thing
        const game = (await saveGame.load(defaults)) ?? defaults;
        this.avatar = game.avatar;
        const userModel = !user.isLoggedIn ? await user.requestLogin() : user.user;
        this.username = userModel?.name ?? game.username;

        console.log('Loaded profile with username: ', this.username);
    }

    save() {
        saveGame.save({
            avatar: this.avatar,
            username: user.user?.name ?? this.username,
        });
    }

    getXP(): number {
        return this.xp;
    }

    addXP(amount: number): void {
        this.xp += amount;
        const p = leaderboards
            .postScore('player-xp', amount)
            .then((s) => {
                this.xp += amount;
            })
            .catch(() => {
                /* Backup, grant the user his xp for one day */
                setCookie('paintball-xp', this.xp.toString(), 1);
            });
    }

    getLevel(): number {
        const xp = this.getXP();
        return getLevelForXP(xp);
    }
}

const profile = new Profile();
export default profile;
